// ------------------
// Storage curves feature
// A storage curve is the water level / volume relation in the sewer system
// This feature expects a table with 2 columns to generate a 2D graph.
// From the table data we create a CSV file so the user can use the data locally
//
// Assumptions:
// - The table has exactly 2 columns; the first is x (water level), the second is y (volume)
// - Column order from the API reflects the intended x/y mapping (relies on object key insertion order)
// - Values are sorted ascending by x
// - The y-axis always starts at 0, but the curve is not necessarily monotonically increasing
// - A storage curve will always have at least 2 data points
// ------------------

import { DownloadIcon } from "@chakra-ui/icons";
import { Flex, Skeleton, useToken } from "@chakra-ui/react";
import { scaleLinear } from "d3-scale";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";

import { Table } from "@nens/rana/api-v1";
import { RanaButton } from "@nens/ranalib";
import { downloadBlob } from "@nens/shared-utils";

import { useFileDescriptorViewQuery } from "@/queries/query-file-descriptors";

// ------------------
// Util functions for table data to CSV data conversion
// ------------------
// Escapes any " characters inside it by doubling them (" → ""). This is required by the CSV spec (RFC 4180)
const formatCsvHeader = (value: string) => `"${value.replace(/"/g, '""')}"`;

const buildCsvString = (xLabel: string, yLabel: string, values: [number, number][]) => {
  const header = `${formatCsvHeader(xLabel)},${formatCsvHeader(yLabel)}\r\n`;
  const rows = values.map(([x, y]) => `${x},${y}\r\n`).join("");
  // UTF-8 BOM ensures Excel opens the file with the correct encoding
  return "\uFEFF" + header + rows;
};

// ------------------
// Fixed variables and util functions for rendering the graph in SVG
// ------------------
const MARGIN = { t: 8, r: 12, b: 34, l: 46 };
// Hint to d3 for the number of axis ticks (labeled marks); d3 may produce more or fewer to keep values "clean" (e.g. round numbers)
const TICK_COUNT = 5;

const getAxisLabel = (column: Table["columns"][string] | undefined) => {
  if (!column) {
    console.error("column data is missing");
    return "";
  }
  return `${column.label ?? column.columnId}${column.unit ? ` (${column.unit.symbol})` : ""}`;
};

export function StorageCurveGraph({
  descriptorId,
  table,
  featureId,
}: {
  descriptorId: string;
  table: Table;
  featureId: string;
}) {
  const { data, isLoading } = useFileDescriptorViewQuery(descriptorId, "gpkg-table", {
    layer_id: table.layerId,
    table_id: table.tableId,
  });
  const values = data?.[featureId];

  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [deepSea] = useToken("colors", ["brand.deep_sea"]);

  const HEIGHT = 200;
  const INNER_WIDTH = width - MARGIN.l - MARGIN.r;
  const INNER_HEIGHT = HEIGHT - MARGIN.t - MARGIN.b;

  useEffect(function computeParentWidth() {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      setWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // On first render, the graph and button are not loaded yet, because the API is called after clicking on the tooltip
  let renderedGraph = null;
  let downloadButton = null;
  const hasValues = !!values && values.length > 0;
  const isReady = hasValues && width > 0;
  const showSkeleton = isLoading || (hasValues && width === 0);

  if (showSkeleton) {
    renderedGraph = <Skeleton h={`${HEIGHT}px`} rounded="base" />;
    downloadButton = (
      <Flex justifyContent="right">
        <Skeleton h={8} w={32} mt={2} rounded="base" />
      </Flex>
    );
  } else if (isReady) {
    const xColumn = table.columns[Object.keys(table.columns)[0]];
    const yColumn = table.columns[Object.keys(table.columns)[1]];
    const xLabel = getAxisLabel(xColumn);
    const yLabel = getAxisLabel(yColumn);

    // Maps x data values (first to last, ascending) to horizontal pixel positions
    const xScale = scaleLinear()
      .domain([values[0][0], values[values.length - 1][0]])
      .range([0, INNER_WIDTH]);

    // Maps y data values (0 to the curve's actual peak, which may not be the last value) to vertical pixel positions, inverted since SVG y grows downward
    const yMax = Math.max(...values.map(([, y]) => y));
    const yScale = scaleLinear().domain([0, yMax]).range([INNER_HEIGHT, 0]);

    const polylinePath = values
      .map(([x, y], i) => `${i === 0 ? "M" : "L"}${xScale(x)},${yScale(y)}`)
      .join(" ");

    const handleDownloadCsv = () => {
      const csvString = buildCsvString(xLabel, yLabel, values);
      downloadBlob(csvString, `storage-curve-${table.tableId}.csv`);
    };

    const renderXAxis = (
      scale: ReturnType<typeof scaleLinear>,
      label: string,
      width: number,
      height: number
    ) => (
      <g transform={`translate(0,${height})`}>
        <line x2={width} stroke="currentColor" strokeWidth={0.5} />
        {scale.ticks(TICK_COUNT).map((tick) => (
          <g key={tick} transform={`translate(${scale(tick)},0)`}>
            <line y2={4} stroke="currentColor" />
            <text y={8} dy="0.71em" textAnchor="middle" fontSize={9} fill="currentColor">
              {tick}
            </text>
          </g>
        ))}
        <text
          x={width / 2}
          y={30}
          textAnchor="middle"
          fontSize={12}
          fill={deepSea}
          fontFamily="Roboto"
          fontWeight="300"
        >
          {label}
        </text>
      </g>
    );

    const renderYAxis = (scale: ReturnType<typeof scaleLinear>, label: string, height: number) => (
      <g>
        <line y2={height} stroke="currentColor" strokeWidth={0.5} />
        {scale.ticks(TICK_COUNT).map((tick) => (
          <g key={tick} transform={`translate(0,${scale(tick)})`}>
            <line x2={-4} stroke="currentColor" />
            <text
              x={-6}
              textAnchor="end"
              dominantBaseline="middle"
              fontSize={9}
              fill="currentColor"
            >
              {tick}
            </text>
          </g>
        ))}
        <text
          transform={`translate(-38,${height / 2}) rotate(-90)`}
          textAnchor="middle"
          fontSize={12}
          fill={deepSea}
          fontFamily="Roboto"
          fontWeight="300"
        >
          {label}
        </text>
      </g>
    );

    renderedGraph = (
      <Flex justifyContent="left">
        <svg width={width} height={HEIGHT} style={{ display: "block" }} overflow="visible">
          <g transform={`translate(${MARGIN.l},${MARGIN.t})`}>
            <path
              d={polylinePath}
              fill="none"
              stroke="var(--chakra-colors-brand-accent)"
              strokeWidth={1.5}
            />
            {renderXAxis(xScale, xLabel, INNER_WIDTH, INNER_HEIGHT)}
            {renderYAxis(yScale, yLabel, INNER_HEIGHT)}
          </g>
        </svg>
      </Flex>
    );

    downloadButton = (
      <Flex justifyContent="right">
        <RanaButton
          size="sm"
          variant="secondary"
          leftIcon={<DownloadIcon />}
          onClick={handleDownloadCsv}
          mt={2}
        >
          <FormattedMessage id="storage_curve.download_CSV" defaultMessage="Download CSV" />
        </RanaButton>
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" ref={containerRef}>
      {renderedGraph}
      {downloadButton}
    </Flex>
  );
}
